import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, getSession } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldWarning, User, Warehouse as WarehouseIcon, MapPin } from "@phosphor-icons/react";

const reasonLabel = {
  outside_area: "Outside Broward / Miami-Dade",
  location_unverified: "Location could not be verified",
  bad_password: "Wrong password",
};

const fmtTime = (ts) =>
  new Date(ts).toLocaleString("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

export default function Activity() {
  const session = getSession();
  const [events, setEvents] = useState(null);

  useEffect(() => {
    api
      .getLogins()
      .then(setEvents)
      .catch(() => toast.error("Failed to load login activity"));
  }, []);

  if (session?.role !== "admin") return <Navigate to="/" replace />;

  const blocked = events?.filter((e) => !e.allowed) ?? [];
  const flagged = events?.filter((e) => e.allowed && e.unusual) ?? [];

  return (
    <main className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="activity-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">
            Login Activity
          </h1>
          <p className="text-base md:text-lg text-slate-500 mt-1">
            Every sign-in attempt. Blocked attempts are highlighted so you can help anyone wrongly locked out.
          </p>
        </div>
        {events && (
          <div className="text-sm font-semibold text-slate-600" data-testid="activity-summary">
            {events.length} attempts · <span className="text-red-600">{blocked.length} blocked</span> · <span className="text-amber-600">{flagged.length} flagged</span>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-md shadow-sm overflow-x-auto">
        {!events ? (
          <div className="p-10 text-center text-slate-500" data-testid="activity-loading">Loading...</div>
        ) : events.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="activity-empty">No login attempts recorded yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="activity-table">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200">
                <th className="px-4 py-3">Time (ET)</th>
                <th className="px-4 py-3">Who</th>
                <th className="px-4 py-3">Warehouse</th>
                <th className="px-4 py-3">Location</th>
                <th className="px-4 py-3">Result</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e, i) => (
                <tr
                  key={i}
                  data-testid={!e.allowed ? "login-row-blocked" : e.unusual ? "login-row-flagged" : "login-row-allowed"}
                  className={`border-b border-slate-100 ${!e.allowed ? "bg-red-50" : e.unusual ? "bg-amber-50" : ""}`}
                >
                  <td className="px-4 py-3 whitespace-nowrap text-slate-600">{fmtTime(e.ts)}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                      <User size={14} weight="duotone" className="text-slate-400" />
                      {e.name}
                      <span className="text-xs font-normal text-slate-500 capitalize">· {e.role}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {e.event === "report_view" ? (
                      <span className="text-slate-500" data-testid="report-view-ref">Report {e.report_date}</span>
                    ) : e.warehouse ? (
                      <span className="inline-flex items-center gap-1">
                        <WarehouseIcon size={14} weight="duotone" className="text-slate-400" />
                        {e.warehouse === "doral" ? "Doral, FL" : "Medley, FL"}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {e.city || e.region ? (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={14} weight="duotone" className="text-slate-400" />
                        {[e.city, e.region].filter(Boolean).join(", ")}
                      </span>
                    ) : (
                      <span className="text-slate-400">Unknown</span>
                    )}
                    {e.ip && <div className="text-[11px] text-slate-400 mt-0.5" data-testid="login-ip">{e.ip}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {e.allowed ? (
                      e.unusual ? (
                        <span
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 border border-amber-300 rounded px-2 py-1"
                          data-testid="flagged-badge"
                        >
                          <ShieldWarning size={13} weight="fill" /> Flagged — outside Miami-Dade / Broward / Kingston
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-2 py-1">
                          <ShieldCheck size={13} weight="fill" /> Allowed
                        </span>
                      )
                    ) : (
                      <span
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-700 bg-red-100 border border-red-200 rounded px-2 py-1"
                        data-testid="blocked-badge"
                      >
                        <ShieldWarning size={13} weight="fill" /> Blocked — {reasonLabel[e.reason] || "Denied"}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </main>
  );
}
